"use client"

import { useEffect, useRef, useState } from "react"
import { usePathname } from "next/navigation"
import { RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

const THRESHOLD = 72 // px of pull needed to trigger a refresh
const MAX_PULL = 120 // clamp the indicator travel
const RESISTANCE = 0.5 // how much finger travel translates to indicator travel

/**
 * Every route where a hard reload could destroy work in progress or is simply
 * never what the gesture means.
 *
 * `/submit` alone was NOT enough: the offline form-fill surfaces are
 * `/offline-submit` (the linked-form shell) and `/offline/<formUrl>` (the offline
 * app shell), and neither startsWith("/submit"). So the gesture stayed live on
 * exactly the screens where a reload is most destructive — a tech mid-inspection
 * with no signal — and reloading there depends on the service worker being able
 * to serve the document, which is precisely the thing you cannot count on when
 * you're standing on a roof.
 */
const NO_REFRESH_PREFIXES = [
  "/submit",
  "/offline-submit",
  "/offline",
  "/operations/map", // a downward pan on the map reads as a pull
  // Chat is live (messages arrive on their own), and dragging down to read back
  // through a conversation kept reloading it.
  "/operations/chat",
  "/m/chat",
  "/m/messages/chat",
]

/**
 * Custom pull-to-refresh for touch devices (iPad Safari standalone + the iPad
 * Capacitor app), where the browser's native gesture doesn't exist — the app is
 * a `standalone` PWA and `globals.css` sets `overscroll-behavior: none`.
 *
 * Mounted app-wide in the root layout, but OFF on the form-fill flows and the
 * map (see NO_REFRESH_PREFIXES), and it NEVER hard-reloads while offline —
 * see onEnd.
 *
 * It only engages when the finger pulls DOWN while the *document* is scrolled to
 * the top. Gestures that start inside an inner scroll container, inside an open
 * dialog, or while an input is focused are ignored — so it never fires mid-form,
 * inside a modal, or inside the floating menu. On release past the threshold it
 * reloads (which also picks up a freshly deployed build / service worker).
 */
export function PullToRefresh() {
  const pathname = usePathname()
  const disabled = NO_REFRESH_PREFIXES.some((p) => pathname?.startsWith(p)) ?? false
  const [pull, setPull] = useState(0)
  const [refreshing, setRefreshing] = useState(false)
  const st = useRef({
    startY: 0,
    tracking: false,
    active: false,
    pull: 0,
    refreshing: false,
    scroller: null as HTMLElement | null,
  })

  useEffect(() => {
    if (disabled) return

    // Resolve which scroller (if any) this gesture belongs to. Walking up from
    // the touch target:
    //   - a container marked `data-ptr-scroller` is *our* scroller (the app
    //     shell scrolls an inner container, not the document) — use it;
    //   - any other scrollable container owns the gesture itself (a modal, a
    //     list) — bail out so we never hijack it;
    //   - reaching the top with no inner scroller falls back to the document,
    //     but only if the document can actually scroll (fixed-height shells
    //     like the app layout can't, so a stray pull on a header does nothing).
    const resolveScroller = (el: Element | null): HTMLElement | null => {
      let node: Element | null = el
      while (node && node !== document.body && node !== document.documentElement) {
        if (node instanceof HTMLElement && node.hasAttribute("data-ptr-scroller")) return node
        const oy = getComputedStyle(node).overflowY
        if ((oy === "auto" || oy === "scroll") && node.scrollHeight > node.clientHeight + 1) {
          return null // an unmarked inner scroller owns this gesture
        }
        node = node.parentElement
      }
      const doc = (document.scrollingElement ?? document.documentElement) as HTMLElement | null
      if (doc && doc.scrollHeight > doc.clientHeight + 1) return doc
      return null
    }

    const atTopOf = (s: HTMLElement | null) => !!s && s.scrollTop <= 0

    const isTypingTarget = (el: Element | null) => {
      const t = el as HTMLElement | null
      if (!t) return false
      const tag = t.tagName
      return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable
    }

    const setPullBoth = (v: number) => {
      st.current.pull = v
      setPull(v)
    }

    const onStart = (e: TouchEvent) => {
      const s = st.current
      if (s.refreshing || e.touches.length !== 1) return
      const target = e.target as Element | null
      // Skip while a dialog/menu is open or while typing.
      if (document.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"], [role="menu"]')) return
      if (isTypingTarget(target)) return
      const scroller = resolveScroller(target)
      if (!atTopOf(scroller)) return // no eligible scroller, or not at its top
      s.scroller = scroller
      s.startY = e.touches[0].clientY
      s.tracking = true
      s.active = false
    }

    const onMove = (e: TouchEvent) => {
      const s = st.current
      if (!s.tracking || s.refreshing) return
      const dy = e.touches[0].clientY - s.startY
      const atTop = atTopOf(s.scroller)
      if (!s.active) {
        if (dy > 6 && atTop) s.active = true
        else {
          if (dy < -6) s.tracking = false // user is scrolling the content up
          return
        }
      }
      if (dy <= 0 || !atTop) {
        s.active = false
        setPullBoth(0)
        return
      }
      // Engaged: this is our gesture — stop the scroller from bouncing.
      e.preventDefault()
      setPullBoth(Math.min(dy * RESISTANCE, MAX_PULL))
    }

    const onEnd = () => {
      const s = st.current
      if (s.active && s.pull >= THRESHOLD && !s.refreshing) {
        // NEVER hard-reload while offline. A reload throws away the running app
        // and asks the service worker to rebuild it from cache — and if anything
        // about that fails (SW not yet controlling, document not cached, a chunk
        // evicted) the tech gets the WebView's error screen instead of their
        // half-finished inspection. There is also nothing to fetch: the whole
        // point of a refresh is to go and get newer data. So: do nothing, say so,
        // and leave the app exactly as it was.
        if (typeof navigator !== "undefined" && navigator.onLine === false) {
          toast("You're offline — nothing to reload.", {
            description: "Your work is saved on this device.",
          })
          setPullBoth(0)
          s.tracking = false
          s.active = false
          return
        }
        s.refreshing = true
        setRefreshing(true)
        setPullBoth(THRESHOLD)
        // Let the spinner paint before the reload wipes the page.
        window.setTimeout(() => window.location.reload(), 150)
      } else if (!s.refreshing) {
        setPullBoth(0)
      }
      s.tracking = false
      s.active = false
    }

    window.addEventListener("touchstart", onStart, { passive: true })
    window.addEventListener("touchmove", onMove, { passive: false })
    window.addEventListener("touchend", onEnd, { passive: true })
    window.addEventListener("touchcancel", onEnd, { passive: true })
    return () => {
      window.removeEventListener("touchstart", onStart)
      window.removeEventListener("touchmove", onMove)
      window.removeEventListener("touchend", onEnd)
      window.removeEventListener("touchcancel", onEnd)
    }
  }, [disabled])

  const progress = Math.min(pull / THRESHOLD, 1)
  /**
   * `disabled` is folded into VISIBILITY, never into whether this component
   * renders an element at all — and that distinction is load-bearing on exactly
   * one screen: the offline cold launch.
   *
   * `disabled` derives from `usePathname()`, and the offline shell is one static
   * document deliberately served at OTHER paths (app/sw.ts serves the precached
   * `/offline` document for a failed navigation to `/`, `/m`, `/m/jobs/<id>`, …
   * — the browser keeps the requested URL). So the two sides disagree:
   * the prerendered HTML was rendered at `/offline`, which IS in
   * NO_REFRESH_PREFIXES, while the hydrating client reads `/` and is NOT. An
   * early `return null` therefore made that a STRUCTURAL mismatch — server
   * <section> (the Toaster) against client <div> — and React threw #418 and
   * regenerated the entire tree on the client, at the slowest and most fragile
   * moment the app has. (Verified on device: two boots, `/` with no network,
   * both "Minified React error #418".)
   *
   * Rendering the same inert element either way makes the markup independent of
   * the pathname, so it hydrates identically wherever the shell lands. It costs
   * nothing: the wrapper is `aria-hidden` and `pointer-events-none`, at zero
   * opacity and zero travel, and the effect above still returns early on
   * `disabled`, so no listener is ever attached on the routes that opt out.
   */
  const visible = !disabled && (pull > 0 || refreshing)

  return (
    <div
      aria-hidden
      className="pointer-events-none fixed inset-x-0 top-0 z-[60] flex justify-center"
      style={{
        transform: `translateY(${refreshing ? THRESHOLD : pull}px)`,
        opacity: visible ? 1 : 0,
        transition: st.current.tracking && !refreshing ? "none" : "transform 0.2s ease, opacity 0.2s ease",
      }}
    >
      <div className="mt-2 flex h-9 w-9 items-center justify-center rounded-full border bg-background shadow-md">
        <RefreshCw
          className={cn("h-4 w-4 text-muted-foreground", refreshing && "animate-spin")}
          style={
            refreshing
              ? undefined
              : { transform: `rotate(${progress * 270}deg)`, opacity: 0.35 + progress * 0.65 }
          }
        />
      </div>
    </div>
  )
}
